<template>
	<div class="boundary-plus-upload">
		<div
			v-if="!show_complete_message"
			ref="dropzone"
			class="boundary-plus-upload__dropzone"
			:style="dropzone_height ? { height: dropzone_height + 'px' } : null"
		>
			<DragDrop
				v-if="uppy"
				:uppy="uppy"
				v-bind="uppy_config"
			/>
			<p
				v-else
				class="boundary-plus-upload__loading"
				:style="dropzone_height ? { height: '100%', minHeight: 'unset' } : null"
			>
				Loading uploader…
			</p>
		</div>

		<div
			v-if="uppy_files.length && !show_complete_message"
			class="boundary-plus-upload__file-list"
		>
			<div
				v-for="file in uppy_files"
				:key="file.id"
				class="boundary-plus-upload__file"
			>
				<img
					v-if="files[file.id] && files[file.id].preview"
					:src="files[file.id].preview"
					class="boundary-plus-upload__file-preview"
					alt=""
				>
				<div
					v-else
					class="boundary-plus-upload__file-icon boundary-plus-upload__file-icon--video"
				/>
				<p class="boundary-plus-upload__file-name">
					{{ file.meta.name }}
				</p>
				<button
					class="boundary-plus-upload__file-remove"
					aria-label="Remove file"
					@click="removeFile( file.id )"
				/>
			</div>
		</div>

		<div
			v-if="uppy_files.length && !uploading && !show_complete_message"
			class="boundary-plus-upload__actions"
		>
			<button
				class="button button--large"
				@click="submitUpload"
			>
				Upload {{ uppy_files.length }} file{{ uppy_files.length === 1 ? '' : 's' }}
			</button>
		</div>

		<div
			class="boundary-plus-upload__progress"
			:class="{ 'invisible': !uploading }"
		>
			<div class="boundary-plus-upload__progress-bar" />
			<div class="boundary-plus-upload__upload-status" />
		</div>

		<div
			v-if="show_complete_message"
			class="boundary-plus-upload__complete"
		>
			<p>Files uploaded. They will appear in the grid once processed.</p>
			<button
				class="button button--large"
				@click="reset"
			>
				Upload more
			</button>
		</div>
	</div>
</template>

<script>
import Uppy from '@uppy/core';
import Transloadit from '@uppy/transloadit';
import { DragDrop } from '@uppy/vue';
import ProgressBar from '@uppy/progress-bar';
import StatusBar from '@uppy/status-bar';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import '@uppy/progress-bar/dist/style.css';
import '@uppy/status-bar/dist/style.css';
import { storeToRefs } from 'pinia';
import moment from 'moment';
import { useDataStore } from '../../stores/data';

let cached_dropzone_height = null;

export default {
	name: 'BoundaryPlusUploadComponent',
	components: {
		DragDrop
	},
	inject: [
		'tl_key',
		'tl_boundary_plus_template_id'
	],
	props: {
		project: { required: true, type: Object }
	},
	setup() {
		const data_store = useDataStore();
		const { user_profile } = storeToRefs( data_store );
		return { user_profile };
	},
	data() {
		return {
			uppy: null,
			uppy_config: {
				height: '100%',
				width: '100%',
				locale: {
					strings: {
						poweredBy: 'The Boundary Live',
						dropHereOr: 'Drop files here or%{browse}',
						browse: 'Browse'
					}
				}
			},
			files: {},
			uploading: false,
			show_complete_message: false,
			expiry_time: null,
			signature: null,
			dropzone_height: cached_dropzone_height,
		};
	},
	computed: {
		uppy_files() {
			if ( !this.uppy ) {
				return [];
			}
			return this.uppy.getFiles();
		},
		uppy_params() {
			return {
				auth: {
					key: this.tl_key,
					expires: this.expiry_time
				},
				template_id: this.tl_boundary_plus_template_id
			};
		},
		assembly_options() {
			return () => ( {
				fields: {
					entry_id: this.project.id,
					uid: this.user_profile.uid,
				},
				waitForEncoding: false,
				params: this.uppy_params,
				signature: `sha1:${this.signature}`
			} );
		}
	},
	mounted() {
		this.getSigningString( this.uppy_params ).then( ( { signature, expiry } ) => {
			this.signature = signature;
			this.expiry_time = expiry;
			this.instantiateUppy();
		} );
	},
	methods: {
		async getSigningString( params ) {
			const expiry = moment.utc().add( 2, 'h' ).format( 'YYYY/MM/DD HH:mm:ss+00:00' );
			params.auth.expires = expiry;
			const response = await this.$axios.post( '/upload/sign', {
				'params': JSON.stringify( params )
			} );
			return { signature: response.data, expiry };
		},
		instantiateUppy() {
			this.uppy = new Uppy( {
				onBeforeFileAdded: this.addFile,
				restrictions: {
					allowedFileTypes: [ '.jpg', '.jpeg', '.png', '.mp4', '.mov', '.webm' ]
				}
			} );
			this.uppy.use( ThumbnailGenerator, {
				thumbnailWidth: 120,
				waitForThumbnailsBeforeUpload: true,
			} );
			this.uppy.use( Transloadit, {
				getAssemblyOptions: this.assembly_options
			} );
			this.uppy.use( ProgressBar, {
				target: '.boundary-plus-upload__progress-bar',
				hideAfterFinish: false
			} );
			this.uppy.use( StatusBar, {
				target: '.boundary-plus-upload__upload-status',
				hideAfterFinish: false,
				showProgressDetails: false,
				hideUploadButton: true,
				hideRetryButton: true,
				hidePauseResumeButton: true,
				hideCancelButton: true,
				locale: {
					strings: {
						upload: 'Uploading',
						complete: 'Upload completed'
					}
				},
			} );
			this.uppy.on( 'thumbnail:generated', ( file, preview ) => {
				if ( this.files[file.id] ) {
					this.files[file.id].preview = preview;
				}
			} );
			this.uppy.on( 'file-removed', ( file ) => {
				if ( file.id in this.files ) {
					delete this.files[file.id];
				}
			} );
			this.uppy.on( 'complete', () => {
				setTimeout( () => {
					this.uploading = false;
					this.show_complete_message = true;
				}, 1500 );
			} );

			this.$nextTick( () => {
				if ( this.$refs.dropzone ) {
					cached_dropzone_height = this.$refs.dropzone.offsetHeight;
					this.dropzone_height = cached_dropzone_height;
				}
			} );
		},
		addFile( new_file ) {
			const existing_files = this.files;
			existing_files[new_file.id] = {
				preview: null,
				filename: new_file.meta.name
			};
			this.files = { ...existing_files };
		},
		removeFile( file_id ) {
			this.uppy.removeFile( file_id );
		},
		submitUpload() {
			this.uploading = true;
			this.uppy.upload();
		},
		reset() {
			this.show_complete_message = false;
			this.files = {};
			this.$emit( 'upload-complete' );
		}
	}
};
</script>
